<template>
  <transition
    name="lm-modal-transition"
    @after-enter="opened"
    @after-leave="closed"
    @leave-cancelled="closed"
  >
    <div
      v-show="show"
      class="Modal"
      :class="{'has-slider': hasSlider}"
      @keydown.esc="close()"
      @mousedown.self="close()"
    >
      <lm-modal-slider
        v-if="hasSlider"
        :class="{'is-disabled': !sliderPrev}"
        icon="navigate_before"
        @click="$emit('prev')"
      />
      <div
        v-focus="show"
        class="Modal-wrapper"
        :class="wrapperClasses"
        :tabindex="tabindex"
      >
        <div
          class="Modal-header"
          :class="bgHeader ? `bg-${bgHeader}` : 'bg-gray-900'"
        >
          <lm-icon v-if="icon" :icon="icon" class="mr-3 text-white" />
          <h1 class="Title">
            {{ title }}
            <slot name="header" />
          </h1>
          <lm-icon-button
            v-if="dismissible && !isForm"
            icon="close"
            class="Modal-closeButton"
            @click="close()"
          />
        </div>
        <slot name="error" />
        <div :id="bodyId" class="Modal-body CommentBoxContainer">
          <slot />
        </div>
        <div v-if="!removeFooter" class="Modal-footer">
          <slot name="footer" />
        </div>
      </div>
      <lm-modal-slider
        v-if="hasSlider"
        :class="{'is-disabled': !sliderNext}"
        icon="navigate_next"
        @click="$emit('next')"
      />
    </div>
  </transition>
</template>

<script>
import LmIcon from '@/components/ui/LmIcon'
import LmIconButton from '@/components/ui/LmIconButton'
import LmModalSlider from '@/components/ui/modal/LmModalSlider'
import {Events, ModalType} from '@/lib/definitions'
import EventHub from '@/lib/eventHub'

export default {
  name: 'LmModal',
  components: {LmIcon, LmModalSlider, LmIconButton},
  directives: {
    focus: {
      update: (el, binding) => {
        if (binding.value) el.focus()
      }
    }
  },
  props: {
    name: {
      type: String,
      required: true
    },
    type: {
      type: String,
      default: ModalType.MODAL
    },
    bodyId: {
      type: String,
      default: null
    },
    title: {
      type: String,
      required: true
    },
    icon: {
      type: String,
      default: ''
    },
    removeFooter: {
      type: Boolean,
      default: false
    },
    fullHeight: {
      type: Boolean,
      default: false
    },
    disableTabindex: {
      type: Boolean,
      default: false
    },
    isForm: {
      type: Boolean,
      default: false
    },
    dismissible: {
      type: Boolean,
      default: true // false === Prevent close from backdrop / header button / escape
    },
    sliderPrev: {
      type: String,
      default: null
    },
    sliderNext: {
      type: String,
      default: null
    },
    bgHeader: {
      type: String,
      default: null
    }
  },
  data() {
    return {
      show: false
    }
  },
  computed: {
    isConfirmation() {
      return this.type === ModalType.DELETE_CONFIRMATION
    },
    hasSlider() {
      return this.sliderPrev || this.sliderNext
    },
    tabindex() {
      return this.disableTabindex ? null : '-1'
    },
    wrapperClasses() {
      return {
        'u-full-height': this.fullHeight && this.type !== ModalType.SUBMODAL,
        'u-submodal-full-height':
          this.fullHeight && this.type === ModalType.SUBMODAL,
        'u-small': this.isConfirmation
      }
    }
  },
  mounted() {
    EventHub.on(Events.CLOSE_MODAL, this.doClose)
    if (!this.isConfirmation) this.open()
  },
  methods: {
    open() {
      if (!this.show) {
        this.$emit(Events.BEFORE_OPEN)
        this.show = true
        this.$store.dispatch('hideScrollbar')
      }
    },
    opened() {
      this.$emit(Events.AFTER_OPEN)
    },
    close(force = false) {
      if (this.isConfirmation) {
        this.doClose(ModalType.DELETE_CONFIRMATION)
      } else if (force || this.dismissible) {
        this.$router.removeKeyFromQuery(this.type)
      }
    },
    doClose(modalType) {
      if (this.type === modalType) {
        this.$emit(Events.BEFORE_CLOSE)
        this.show = false
      }
    },
    closed() {
      this.$emit(Events.AFTER_CLOSE)
      EventHub.removeListener(Events.CLOSE_MODAL, this.doClose)
      EventHub.emit(Events.MODAL_CLOSED, this.name)
      this.$store.dispatch('showScrollbar')
    }
  }
}
</script>

<style scoped>
.Modal {
  --h-margin: 40px;
  position: fixed;
  z-index: theme('zIndex.modal');
  top: 0;
  bottom: 0;
  left: 0;
  right: 0;
  display: flex;
  justify-content: center;
  align-items: center;
  background-color: theme('backgroundColor.backdrop');
  &.has-slider {
    justify-content: space-between;
    .Modal-wrapper {
      margin: 0;
    }
  }
}
.Modal-wrapper {
  position: relative;
  width: 660px;
  max-height: calc(100% - var(--h-margin));
  margin: 0 40px;
  display: flex;
  flex-direction: column;
  background-color: #ffffff;
  transform: scale(1);
  box-shadow: 0 2px 10px 0 rgba(45, 55, 72, 0.4);
  &:focus {
    outline: none;
  }
  &.u-full-height,
  &.u-submodal-full-height {
    .Modal-body {
      height: 100%;
    }
  }
  &.u-full-height {
    height: calc(100% - var(--h-margin));
  }
  &.u-submodal-full-height {
    height: calc(100% - var(--h-margin) * 2);
    width: 600px;
  }
  &.u-small {
    width: 600px;
    .Modal-body {
      height: 300px;
    }
  }
}

.Modal-header,
.Modal-body {
  padding: 24px;
}
.Modal-header,
.Modal-footer {
  display: flex;
  align-items: center;
}
.Modal-header {
  min-height: 72px;
  .Title {
    margin-right: 16px;
    line-height: 1;
    font-size: theme('fontSize.s');
    font-family: theme('fontFamily.mono');
    text-transform: uppercase;
    color: white;
  }
  .Modal-closeButton {
    margin-left: auto;
    color: white;
  }
}
.Modal-body {
  height: 400px;
  overflow-y: auto;
  p {
    margin-top: 0;
  }
}
.Modal-footer {
  padding: 16px 24px;
  justify-content: flex-end;
  border-top: 1px solid #dcdcdc;
}
@media (--mobile) {
  .Modal-wrapper,
  .Modal-wrapper.u-full-height,
  .Modal-wrapper.u-submodal-full-height,
  .Modal-wrapper.u-small {
    width: 100%;
    height: 100%;
    margin: 0;
    max-height: 100%;
  }
  .Modal-header,
  .Modal-body,
  .Modal-footer {
    padding: 16px;
  }
  .Modal-header,
  .Modal-footer {
    min-height: 50px;
  }
  .Modal-header {
    z-index: 2;
    padding-right: 16px; /* Close button offset */
    box-shadow: 0 2px 10px 0 rgba(153, 153, 153, 0.5);
  }
  .Modal-body,
  .Modal-wrapper.u-small .Modal-body {
    z-index: 1;
    height: 100%;
  }
}
/* Transition */
.lm-modal-transition-enter-active {
  transition: opacity 0.5s;
  .Modal-wrapper {
    transition: transform 0.5s;
  }
}
.lm-modal-transition-leave-active {
  transition: opacity 0.3s;
  .Modal-wrapper {
    transition: transform 0.3s;
  }
}
.lm-modal-transition-enter,
.lm-modal-transition-leave-to {
  opacity: 0;
}
.lm-modal-transition-enter .Modal-wrapper {
  transform: scale(0.8);
}
.lm-modal-transition-leave-to .Modal-wrapper {
  transform: scale(1.2);
}
</style>
